import { useEffect, useRef } from "react";
import useAuthHook from "./useAuthHook";
import { Jwt } from "../utils/interfaces";

function usePaymentLeaveGuard({
  enabled = true,
  orderId,
  branchId,
  sessionId,
  ledgerId,
  isSplitPayment,
}: {
  enabled?: boolean;
  orderId: number;
  branchId: number;
  sessionId: number;
  ledgerId: number;
  isSplitPayment: boolean;
}) {
  const { jwt } = useAuthHook();
  const enabledRef = useRef(enabled);
  const orderIdRef = useRef(orderId);
  const branchIdRef = useRef(branchId);
  const sessionIdRef = useRef(sessionId);
  const splitPaymentRef = useRef(isSplitPayment);
  const ledgerIdRef = useRef(ledgerId);
  const jwtRef = useRef(jwt);

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);
  useEffect(() => {
    orderIdRef.current = orderId;
  }, [orderId]);
  useEffect(() => {
    branchIdRef.current = branchId;
  }, [branchId]);
  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);
  useEffect(() => {
    jwtRef.current = jwt;
  }, [jwt]);
  useEffect(() => {
    splitPaymentRef.current = isSplitPayment;
  }, [isSplitPayment]);
  useEffect(() => {
    ledgerIdRef.current = ledgerId;
  }, [ledgerId]);

  useEffect(() => {
    const sendAbort = () => {
      if (!enabledRef.current || !jwtRef.current) return;
      const token = (jwtRef.current as Jwt).token;
      const baseUrl = import.meta.env.VITE_API_SERVER ?? "";
      const isSession = sessionIdRef.current ? true : false;

      const url = isSplitPayment
        ? isSession
          ? `internal/secure/pos/v1/payments/pos-terminal-abort/session_splitpayment/${ledgerIdRef.current}/${orderIdRef.current}/${branchId}`
          : `internal/secure/pos/v1/payments/pos-terminal-abort/splitpayment/${ledgerIdRef.current}/${orderIdRef.current}/${branchId}`
        : isSession
          ? `${baseUrl}/internal/secure/pos/v1/payments/pos-terminal-abort/session/${sessionIdRef.current}/${branchIdRef.current}`
          : `${baseUrl}/internal/secure/pos/v1/payments/pos-terminal-abort/order/${orderIdRef.current}/${branchIdRef.current}`;

      fetch(url, {
        method: "POST",
        keepalive: true,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
    };

    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!enabledRef.current) return;
      e.preventDefault();
      e.returnValue = "";
    };

    const onPageHide = (e: PageTransitionEvent) => {
      if (!e.persisted) {
        sendAbort();
      }
    };

    window.addEventListener("beforeunload", onBeforeUnload);
    window.addEventListener("pagehide", onPageHide);

    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      window.removeEventListener("pagehide", onPageHide);
    };
  }, []);
}

export default usePaymentLeaveGuard;
